import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DotsVerticalIcon } from 'assets/icons/dots-vertical.svg';
import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import { BUTTON_VARIANT, ICON_BUTTON_SIZES } from 'constants/interfaces';

import type IAnalysisResultCardHeader from './interfaces/IAnalysisResultCardHeader';

import styles from './sass/AnalysisResultCardHeader.module.scss';

function AnalysisResultCardHeader({
  formattedDate,
  onDelete,
  onDuplicate,
  onToggleVisibility,
  showVisibilityButton = false,
  isVisibilityActive = false,
}: IAnalysisResultCardHeader): React.JSX.Element {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleMenuAction = (action?: () => void): void => {
    setIsMenuOpen(false);
    action?.();
  };

  const handleToggleVisibility = (): void => {
    onToggleVisibility?.();
  };

  return (
    <div className={styles.header}>
      <span className={styles.date}>{formattedDate}</span>
      <div className={styles.headerActions}>
        {showVisibilityButton ? (
          <>
            <IconButton
              icon={<EyeIcon aria-hidden="true" />}
              onClick={handleToggleVisibility}
              size={ICON_BUTTON_SIZES.small}
              ariaLabel={t('valueChainMap.resultCardToggleVisibility')}
              className={clsx(
                styles.headerIconButton,
                styles.headerIconButtonPrimary,
                { [styles.headerIconButtonActive]: isVisibilityActive },
              )}
            />
            <div ref={menuRef} className={styles.menuWrapper}>
              <IconButton
                icon={<DotsVerticalIcon />}
                onClick={() => setIsMenuOpen((prev) => !prev)}
                size={ICON_BUTTON_SIZES.small}
                ariaLabel={t('valueChainMap.resultCardOpenMenu')}
                className={clsx(styles.headerIconButton, {
                  [styles.headerIconButtonActive]: isMenuOpen,
                })}
              />
              {isMenuOpen ? (
                <div className={styles.menu} role="menu">
                  <Button
                    type="button"
                    role="menuitem"
                    variant={BUTTON_VARIANT.ghost}
                    title={t('valueChainMap.resultCardReRun')}
                    className={styles.menuItem}
                    onClick={() => handleMenuAction(onDuplicate)}
                  />
                  <Button
                    type="button"
                    role="menuitem"
                    variant={BUTTON_VARIANT.ghost}
                    title={t('common.delete')}
                    className={clsx(styles.menuItem, styles.menuItemDanger)}
                    onClick={() => handleMenuAction(onDelete)}
                  />
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default AnalysisResultCardHeader;
